import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Scale, Sun, Moon, Languages, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const { t, language, toggleLanguage, direction } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Initialize system on first load
    const initSystem = async () => {
      try {
        const response = await authAPI.init();
        if (response.data.initialized) {
          setInitialized(true);
          toast.success(
            language === 'ar' 
              ? 'تم تهيئة النظام. المستخدم: admin / كلمة المرور: admin123'
              : 'System initialized. Username: admin / Password: admin123'
          );
        }
      } catch (error) {
        console.log('System already initialized or error:', error);
      }
    };
    initSystem();
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      toast.success(language === 'ar' ? 'تم تسجيل الدخول بنجاح' : 'Login successful');
      navigate('/');
    } catch (err) {
      setError(t('loginError'));
      toast.error(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted"
      dir={direction}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1571055931484-22dce9d6c510?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Theme & Language Controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="gap-2"
          data-testid="login-language-toggle"
        >
          <Languages className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">{language === 'en' ? 'العربية' : 'English'}</span>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="login-theme-toggle"
        >
          {isDark ? <Sun className="w-5 h-5" strokeWidth={1.5} /> : <Moon className="w-5 h-5" strokeWidth={1.5} />}
        </Button>
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-md z-10 border-border/50 shadow-xl fade-in">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-24 h-24 rounded-full overflow-hidden bg-white shadow-lg flex items-center justify-center">
            <img src="/logo.png" alt="شركة التمكين" className="w-20 h-20 object-contain" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">
              {language === 'ar' ? 'عملاء التمكين' : 'Tamkeen Clients'}
            </CardTitle>
            <CardDescription className="mt-2">
              {t('signInToContinue')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-sm bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username">{t('username')}</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={language === 'ar' ? 'أدخل اسم المستخدم' : 'Enter username'}
                required
                className="h-11"
                data-testid="username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={language === 'ar' ? 'أدخل كلمة المرور' : 'Enter password'}
                required
                className="h-11"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 btn-accent"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('loading')}
                </span>
              ) : (
                t('loginButton')
              )}
            </Button>
          </form>

          {initialized && (
            <div className="mt-4 p-3 rounded-sm bg-muted text-sm text-center">
              <p className="text-muted-foreground">
                {language === 'ar' 
                  ? 'بيانات الدخول الافتراضية: admin / admin123'
                  : 'Default credentials: admin / admin123'}
              </p>
            </div>
          )}

          <div className="mt-4 text-center text-sm text-muted-foreground">
            {language === 'ar' ? 'عميل جديد؟ ' : 'New client? '}
            <Link to="/register" className="text-[hsl(var(--accent))] hover:underline font-medium">
              {language === 'ar' ? 'سجّل من هنا' : 'Register here'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
